import { Platform } from 'react-native';
import { LinkingOptions } from '@react-navigation/native';

// Sur DESKTOP web, les écrans de détail vivent dans le stack INTERNE de `DesktopLayout` :
// c'est lui qui porte le menu de gauche et l'en-tête. Mappés au niveau du stack RACINE, un
// deep-link (notification, URL collée, rafraîchissement) les rendait HORS du layout — barre
// latérale de mission présente, menu de l'app absent.
// Sur mobile (et web étroit), `Main` est le TabNavigator, qui n'a pas ces écrans : ils
// restent alors à la racine, où le flux mobile les empile.
const isDesktopWeb = Platform.OS === 'web'
  && typeof window !== 'undefined' && window.innerWidth >= 768;

// Params NUMÉRIQUES à reconvertir en nombre (CCT-252).
//
// POURQUOI. Une URL ne transporte que du texte : `?missionId=3` arrive dans
// `route.params` comme la CHAÎNE « 3 ». En navigation interne, le même écran reçoit le
// NOMBRE 3. Les deux chemins livrent donc des types différents pour le même paramètre, et
// tout le code écrit en `===` bascule silencieusement :
//   `mockChatData.find(d => d.missionId === missionId)` → `3 === "3"` → false → « Mission
//   introuvable » sur un écran qui marche parfaitement quand on y arrive par un clic.
// Le défaut ne se manifeste donc QUE par URL collée, deep-link ou F5 — c'est-à-dire
// exactement ce qu'on ne teste pas en cliquant.
//
// On répare à la SEULE frontière où la conversion a lieu, plutôt que dans chaque écran.
//
// PÉRIMÈTRE VOLONTAIREMENT ÉTROIT : seulement `missionId` et `chapterId`. Surtout PAS
// `elementId` ni `id`, qui portent des identifiants TEXTUELS (« el-42 », « chapter-1 ») —
// les passer à `Number` donnerait `NaN` et casserait des écrans qui marchent.
const NUM = { missionId: Number, chapterId: Number } as const;

const DETAIL_SCREENS = {
  Chapters: 'chapters',
  ChapterDetail: { path: 'chapter/:id?', parse: NUM },
  MissionCards: 'mission-cards',
  UserStories: 'user-stories',
  UserStoryDetail: 'user-story/:id?',
  // Push du moteur : /us-chat/{missionId}/{elementId} (CCT-74).
  UsChat: { path: 'us-chat/:missionId/:elementId', parse: NUM },
  MissionChat: { path: 'mission-chat', parse: NUM },
  Quiz: { path: 'quiz', parse: NUM },
  MissionDetail: { path: 'mission/:missionId?', parse: NUM },
  SceneReader: { path: 'scene-reader/:id?', parse: NUM },
  TaskDetail: 'task/:id?',
  TaskBoard: 'task-board',
} as const;

// Écrans « Espace entreprise » : ILS EXISTENT AUSSI dans le stack interne de `DesktopLayout`
// (donc avec le menu). Mappés à la RACINE, un refresh/deep-link sur /company les rendait HORS
// layout (menu absent) — même symptôme que les DETAIL_SCREENS. On les mappe donc SOUS `Main`
// en desktop web, et à la racine seulement hors desktop (flux mobile).
const COMPANY_SCREENS = {
  MyInformation: 'my-information',
  CompanyAdmin: 'company',
  CompanyDetail: 'company/:companyId',
  MyInvitations: 'invitations',
} as const;

const prefixes = [
  'ccjaw://',
  'http://localhost:3000',
  'https://dev.cloudchrono.com',
  'https://main.cloudchrono.com',
  'https://cloudchrono.com',
];

export const linking: LinkingOptions<any> = {
  prefixes,
  config: {
    screens: {
      Invite: 'invite/:token',
      Main: {
        screens: {
          Missions: '',
          Chat: 'chat',
          Notifications: 'notifications',
          Account: 'account',
          ...(isDesktopWeb ? { ...DETAIL_SCREENS, ...COMPANY_SCREENS } : {}),
        },
      },
      Cart: 'cart',
      // ⚠️ Ne PAS ajouter ici une seconde entrée `MissionDetail` pour le stack public : deux
      // écrans sur le même motif font échouer react-navigation au démarrage (« Found conflicting
      // screens with the same pattern ») — écran d'erreur, application morte. Le stack public
      // résout la fiche autrement : route initiale + `initialParams` déduits de l'URL
      // (`app/index.tsx`), et l'écran réaligne l'URL sur `/mission/{id}` une fois monté.
      CardDiscussion: 'card-discussion/:cardId?',
      OrderPipeline: 'order-pipeline',
      // Écrans de détail + Espace entreprise : à la RACINE seulement HORS desktop web (flux
      // mobile). En desktop web ils sont sous `Main` (ci-dessus) pour conserver le menu.
      ...(isDesktopWeb ? {} : { ...DETAIL_SCREENS, ...COMPANY_SCREENS }),
      // CCT-48 : autres écrans du root stack — mêmes crashes deep-link sans path
      // (react-navigation utilise le nom de route comme URL, non re-parsable).
    },
  },
};
